import { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useNavigate } from "react-router-dom";
import AuthModal from "../components/AuthModal";
import SubscribeSheet from "../components/SubscribeSheet";
import CosmicBackground from "../components/CosmicBackground";
import { useAuth } from "../context/AuthContext";
import { usePricing } from "../context/PricingContext";
import { savePendingGuestQuestion } from "../hooks/useGuestChat";
import { useCareerMotion } from "./useCareerMotion";
import "./MarriagePage.css";

/**
 * /marriage, marriage timing and kundli milan landing page.
 *
 * WHY THIS PAGE EXISTS SEPARATELY, and where its boundary is. Three pages
 * already carry "marriage" in their title, so this one is scoped deliberately:
 *
 *   /kundli-matching     owns gun milan as a CALCULATOR: enter two charts, get
 *                        a 36-guna score. Highest-traffic page on the site.
 *   /thirumana-porutham  the Tamil 10-porutham equivalent.
 *   /love-astrology      owns love and relationship reading.
 *   /marriage  (this)    owns marriage TIMING ("shaadi kab hogi") and the
 *                        rishta DECISION ("ye rishta sahi hai?"). It reads a
 *                        score rather than computing one.
 *
 * If that boundary blurs, this page will cannibalise /kundli-matching, which
 * is the last thing we want. Keep the H1 and title on timing and decision, and
 * link the scoring itself out to /kundli-matching rather than rebuilding it.
 *
 * Everything structural here (the --label type system, the chip grid, the
 * motion hook, FAQ schema built from the rendered markup) is carried over from
 * CareerPage rather than re-derived, including the fixes those pages needed.
 * See MarriagePage.css for why every selector is scoped under .mrg-root.
 *
 * RETARGET 2026-10-13 (item 20): GSC 28d gave the marriage cluster 87 clicks /
 * 782 imp @11.5, but the HOMEPAGE owned the head: exact "ai marriage
 * prediction" pages were homepage 18 clicks / 226 imp @7.4 vs this page
 * 0 / 2 @6.0. The strongest-CTR modifiers were "ai marriage prediction free"
 * 12 / 71 @4.2 (16.9%) and "ai marriage prediction by date of birth" 12 / 50
 * @10.7 (24%; 50% on this page when shown). Title/og/description/sub now lead
 * with "AI Marriage Prediction by Date of Birth, Free". The Hinglish H1 stays:
 * "ai, meri shaadi kab hogi?" already ranks 1.0 here and that identity is the
 * page's long tail.
 */

/* Chip and CTA key -> the question actually asked. The click handler reads
   these keys, never the visible label, so labels can be shortened for layout
   without changing what gets sent. */
const QUESTIONS: Record<string, string> = {
  kab: "Meri shaadi kab hogi? Mere chart mein 7th house, Venus aur dasha ke hisaab se favourable window kab hai?",
  "rishta-haan":
    "Mere liye ek rishta aaya hai. Kundli ke hisaab se ye rishta sahi hai ya nahi, aur haan bolne se pehle kya dekhna chahiye?",
  gun: "Humare kundli milan mein kitne gun mile hain, aur har koota ka matlab kya hai?",
  manglik:
    "Main manglik hoon. Mere chart mein Mangal dosha kitna strong hai, aur traditional parihar conditions kya hain?",
  "love-family":
    "Humara love marriage hai par kundli nahi mili. Score kahan se kam hua, aur uska traditional matlab kya hai?",
  delay:
    "Meri shaadi mein itni der kyun ho rahi hai? Chart mein kya indicate karta hai aur favourable period kab hai?",
  parents:
    "Mere bachche ke liye kai rishte hain. Unki kundliyan compare karke batao kaunsa match behtar hai aur kyun.",
};

/* Built from the rendered <details> below, never hand-maintained alongside it.
   FAQPage markup that declares questions with no visible counterpart is
   Google's "unrepresentative markup" violation, which is exactly the defect
   /career and /love-astrology both shipped with. Any edit to the FAQ list must
   be made in BOTH places, or the markup goes invalid again. */
const FAQ_SCHEMA: Array<[string, string]> = [
  [
    "Kya ye meri shaadi ki exact date batayega?",
    "Nahi. Vedic timing favourable windows dikhati hai, fixed dates nahi. Aura batata hai ki kaunsa period shaadi ke liye traditionally favourable hai, aur kyun.",
  ],
  [
    "Main manglik hoon. Kya meri shaadi mein problem hogi?",
    "Mangal dosha bahut common hai, aur iske kai traditional parihar maane jaate hain. Aura tumhara exact placement aur parihar conditions dikhata hai. Ye tumhari shaadi ka faisla nahi karta, sirf samjhata hai.",
  ],
  [
    "Kundli nahi mili. Shaadi karein ya nahi?",
    "Aura batata hai ki score kahan se kam hua aur uska traditional matlab kya hai. Faisla tumhara aur tumhari family ka hai. Kundli ek pehlu hai, poori kahani nahi.",
  ],
  [
    "Kya ye bata sakta hai ki ladka ya ladki achha insaan hai?",
    "Nahi. Aura dono charts ki compatibility indications padhta hai, kisi ka character ya private life nahi. Insaan ko jaanne ke liye milna aur baat karna zaroori hai.",
  ],
  [
    "Birth time nahi pata?",
    "Chalega. Approximate time se bhi chart ban jaata hai aur zyada tar cheezein padhi ja sakti hain. Exact time hone se lagna aur dasha timing sharper hoti hai, isliye jitna paas ho sake utna accurate time daalo.",
  ],
  [
    "Free sawaal ke baad kya?",
    "Tumhe poora jawaab milta hai. Agar timing windows, Mangal dosha analysis aur poora kundli milan chahiye, toh Marriage Report mein milega.",
  ],
  [
    "App download karna padega?",
    "Nahi. Yahin browser mein chalta hai. Android app bhi hai.",
  ],
  [
    "When will I get married according to my birth chart?",
    "Vedic astrology reads marriage timing from the 7th house and its lord, Venus and Jupiter as the marriage karakas, the Navamsa or D9 divisional chart, and the Vimshottari dasha period you are currently running. Together these mark favourable windows rather than a fixed date. Any service offering you an exact wedding date from a birth chart is guessing.",
  ],
  [
    "What is marriage prediction by date of birth?",
    "A marriage prediction needs the date, the time and the place of birth, not the date alone. Those three fix the lagna, the house cusps and the dasha sequence, which is where the timing comes from. With only a date you get the planetary signs but not the houses, and the 7th house is what makes it a marriage reading.",
  ],
  [
    "Is AI marriage prediction free and accurate?",
    "Your first question to Aura AI is free, with no card. Accuracy comes from the chart work: Aura computes your kundli with Swiss Ephemeris astronomy, then reads the 7th house, Venus, Jupiter, Navamsa and dashas the classical Vedic way. It shows traditionally favourable windows and compatibility indications, never fixed dates or guarantees.",
  ],
];

const MarriagePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const rootRef = useRef<HTMLDivElement>(null);

  useCareerMotion(rootRef);

  const [authOpen, setAuthOpen] = useState(false);
  const [pendingReport, setPendingReport] = useState(false);
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [pendingSubscribe, setPendingSubscribe] = useState(false);

  const {
    user,
    signInWithGoogle,
    requestPhoneOtp,
    verifyPhoneOtp,
    requestFirebasePhoneOtp,
    verifyFirebasePhoneOtp,
    verifyMsg91Otp,
    pricingVariant,
  } = useAuth();

  /* Live price, read rather than hardcoded so it follows the visitor's currency
     and any member rate. The design shipped a dashed "price" placeholder chip;
     we print the real figure instead. */
  const { prices, formatPrice } = usePricing();
  const reportPrice = prices["report_premium"] ?? 0;

  const phoneAuthHandlers = useMemo(
    () => ({ requestOtp: requestPhoneOtp, verifyOtp: verifyPhoneOtp }),
    [requestPhoneOtp, verifyPhoneOtp],
  );
  const firebasePhoneAuthHandlers = useMemo(
    () => ({
      requestOtp: requestFirebasePhoneOtp,
      verifyOtp: verifyFirebasePhoneOtp,
    }),
    [requestFirebasePhoneOtp, verifyFirebasePhoneOtp],
  );
  const msg91AuthHandlers = useMemo(
    () => ({
      verifyOtp: (payload: {
        accessToken: string;
        phone: string;
        firstName?: string;
        lastName?: string;
        email?: string;
      }) => verifyMsg91Otp({ ...payload, variant: pricingVariant }),
    }),
    [verifyMsg91Otp, pricingVariant],
  );

  /* Park the question, then route by auth state. A guest gets the login sheet
     HERE rather than a trip to /chat: ChatRoute sends a logged-out INDIAN
     visitor to the homepage, and this page is Hinglish and written for exactly
     that visitor, so navigating them to /chat would throw away the click and
     the parked question with it. */
  const ask = (question?: string) => {
    if (question) savePendingGuestQuestion(question);
    if (user) {
      navigate("/chat");
      return;
    }
    setAuthOpen(true);
  };

  /* The report CTA opens the premium report on /reports, the same one
     ReportUpsellCard links to. /reports is behind ProtectedRoute, so a guest
     signs in first and the effect below carries them the rest of the way. */
  const openMarriageReport = () => {
    if (user) {
      navigate("/reports", { state: { activeTab: "ai", fromProceed: true } });
      return;
    }
    setPendingReport(true);
    setAuthOpen(true);
  };

  const openMembership = () => {
    if (user) {
      setSubscribeOpen(true);
      return;
    }
    setPendingSubscribe(true);
    setAuthOpen(true);
  };

  useEffect(() => {
    if (user && pendingReport) {
      setPendingReport(false);
      navigate("/reports", { state: { activeTab: "ai", fromProceed: true } });
    }
  }, [user, pendingReport, navigate]);

  useEffect(() => {
    if (user && pendingSubscribe) {
      setPendingSubscribe(false);
      setSubscribeOpen(true);
    }
  }, [user, pendingSubscribe]);

  const showPath = (key: string) => {
    const root = rootRef.current;
    if (!root) return;
    root.querySelectorAll<HTMLElement>(".tab").forEach((t) => {
      const on = t.dataset.path === key;
      t.setAttribute("aria-selected", String(on));
      t.tabIndex = on ? 0 : -1;
      const panel = root.querySelector<HTMLElement>("#p-" + t.dataset.path);
      if (panel) panel.hidden = !on;
    });
  };

  /* ?q= preselects a question, ?path= preselects a tab, so a campaign can point
     at one variant. */
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const path = p.get("path");
    if (path && rootRef.current?.querySelector("#p-" + path)) showPath(path);
    const q = p.get("q");
    if (q && QUESTIONS[q]) {
      rootRef.current
        ?.querySelectorAll<HTMLElement>(".chip")
        .forEach((c) => c.setAttribute("aria-pressed", String(c.dataset.q === q)));
    }
  }, [location.search]);

  /* One delegated handler, so the ported markup needs no per-button props. */
  const onClick = (e: React.MouseEvent) => {
    const el = e.target as HTMLElement;
    const tab = el.closest<HTMLElement>(".tab");
    if (tab?.dataset.path) {
      showPath(tab.dataset.path);
      return;
    }
    const chip = el.closest<HTMLElement>(".chip");
    if (chip?.dataset.q) {
      ask(QUESTIONS[chip.dataset.q]);
      return;
    }
    if (el.closest("[data-report]")) {
      openMarriageReport();
      return;
    }
    if (el.closest("[data-subscribe]")) {
      openMembership();
      return;
    }
    const cta = el.closest<HTMLElement>("[data-ask]");
    if (cta) {
      ask(cta.dataset.preset ? QUESTIONS[cta.dataset.preset] : undefined);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    const tab = (e.target as HTMLElement).closest<HTMLElement>(".tab");
    if (!tab || !rootRef.current) return;
    const d = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
    if (!d) return;
    e.preventDefault();
    const tabs = Array.from(rootRef.current.querySelectorAll<HTMLElement>(".tab"));
    const i = tabs.indexOf(tab);
    const next = tabs[(i + d + tabs.length) % tabs.length];
    if (next?.dataset.path) {
      showPath(next.dataset.path);
      next.focus();
    }
  };

  return (
    <div className="mrg-root" ref={rootRef} onClick={onClick} onKeyDown={onKeyDown}>
      <CosmicBackground />
      <Helmet>
        <title>AI Marriage Prediction by Date of Birth, Free | Aura AI</title>
        <meta
          name="description"
          content="Free AI marriage prediction by date of birth. Shaadi kab hogi, rishta sahi hai? Aura AI reads your 7th house, Venus, Navamsa and dashas for favourable windows."
        />
        <link rel="canonical" href="https://astroaura.ai/marriage/" />
        <meta
          property="og:title"
          content="AI Marriage Prediction by Date of Birth, Free | Aura AI"
        />
        <meta
          property="og:description"
          content="Shaadi kab? Kisse? Aur ye rishta sahi hai? Ask Aura AI using your exact birth chart. First question free."
        />
        <meta property="og:url" content="https://astroaura.ai/marriage/" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ_SCHEMA.map(([q, a]) => ({
              "@type": "Question",
              name: q,
              acceptedAnswer: { "@type": "Answer", text: a },
            })),
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Aura AI", item: "https://astroaura.ai/" },
              {
                "@type": "ListItem",
                position: 2,
                name: "AI Marriage Prediction",
                item: "https://astroaura.ai/marriage/",
              },
            ],
          })}
        </script>
      </Helmet>

      <main>
        {/* HERO */}
        <section className="hero" aria-labelledby="h1">
          <div className="wrap">
            <div>
              <div className="trust"><span><b>★ 4.4</b> on Google Play</span><span><b>1M+</b> downloads</span><span>Private &amp; encrypted</span></div>
              <h1 id="h1">Shaadi kab? Kisse? <em>Aur ye rishta sahi hai ya nahi?</em></h1>
              <p className="sub"><strong>Free AI marriage prediction from your exact birth chart.</strong> Aura tumhare <strong>7th house, Venus, Jupiter, Navamsa (D9) aur dashas</strong> ek saath padhta hai. Kundli milan mein sirf score nahi, uska matlab bhi samjhata hai.</p>
              <button className="btn" type="button" data-ask>Apna Shaadi Sawaal Poochho, FREE <span aria-hidden="true">&rarr;</span></button>
              <div className="micro"><span>Pehla sawaal bilkul free</span><span>Koi card nahi</span><span>60 second</span></div>

              <p className="chips-label" id="chips-label">Ya inme se chuno</p>
              <div className="chips" role="group" aria-labelledby="chips-label">
                <button className="chip" type="button" data-q="kab">Meri shaadi kab hogi?</button>
                <button className="chip" type="button" data-q="rishta-haan">Rishta sahi hai?</button>
                <button className="chip" type="button" data-q="gun">Kitne gun mile?</button>
                <button className="chip" type="button" data-q="manglik">Main manglik hoon</button>
                <button className="chip" type="button" data-q="love-family">Kundli nahi mili</button>
                <button className="chip" type="button" data-q="delay">Itni der kyun?</button>
              </div>
            </div>

            <aside className="reading" aria-label="Sample reading">
              <div className="reading-head"><span className="tag">Sample reading</span><span className="tag">Hinglish</span></div>
              <div className="kundli">
                <svg viewBox="0 0 300 300" role="img" aria-label="Sample North Indian kundli with the 7th house highlighted">
                  <rect x="1" y="1" width="298" height="298" fill="#140718" stroke="#44264b" strokeWidth="2"/>
                  <polygon points="150,150 225,225 150,300 75,225" fill="rgba(255,204,253,.18)"/>
                  <g stroke="#44264b" strokeWidth="2" fill="none">
                    <line x1="0" y1="0" x2="300" y2="300"/><line x1="300" y1="0" x2="0" y2="300"/>
                    <polygon points="150,0 300,150 150,300 0,150"/>
                  </g>
                  <polygon points="150,150 225,225 150,300 75,225" fill="none" stroke="#ffccfd" strokeWidth="2.5"/>
                  <g fontFamily="Inter, system-ui, sans-serif" fontSize="15" fill="#8d7593" textAnchor="middle">
                    <text x="150" y="60">1</text><text x="75" y="22">2</text><text x="22" y="80">3</text><text x="75" y="140">4</text>
                    <text x="22" y="230">5</text><text x="75" y="292">6</text><text x="150" y="212" fill="#ffccfd">7</text><text x="225" y="292">8</text>
                    <text x="278" y="200">9</text><text x="225" y="140">10</text><text x="278" y="80">11</text><text x="225" y="22">12</text>
                  </g>
                  <g fontFamily="Space Grotesk, system-ui, sans-serif" fontSize="17" fontWeight="700" textAnchor="middle">
                    <text x="276" y="232" fill="#f2cf8a">Ju</text>
                    <text x="150" y="250" fill="#ffccfd">Ve</text>
                    <text x="75" y="170" fill="#f7eef8">Mo</text>
                    <text x="225" y="170" fill="#f7eef8">Sa</text>
                    <text x="150" y="90" fill="#bfa9c4">Ra</text>
                  </g>
                </svg>
                <dl>
                  <div><dt>7th lord</dt><dd><span className="hl">Jupiter</span> in the 9th</dd></div>
                  <div><dt>Navamsa (D9)</dt><dd>Venus strong</dd></div>
                  <div><dt>Next antardasha</dt><dd>Jupiter</dd></div>
                </dl>
              </div>
              <div className="msg user">29 ki ho gayi hoon, ghar wale roz rishte dikha rahe hain. Shaadi kab tak hogi?</div>
              <div className="msg aura"><span className="who">Aura AI</span>Tumhara 7th lord Jupiter 9th house mein hai, aur Navamsa mein Venus strong hai. Abhi Saturn-Rahu chal raha hai, jise traditionally commitment ke liye slow phase maana jaata hai. <strong>Jupiter antardasha</strong> agle saal shuru hota hai, jo shaadi ke liye traditionally <strong>favourable window</strong> hai. Tab tak pressure mein haan bolne se better hai ye clear karna ki tumhe kya chahiye.</div>
              <p className="reading-foot">Example for illustration &middot; Readings show windows, not fixed dates</p>
            </aside>
          </div>
        </section>

        {/* PATHS */}
        <section className="rule" aria-labelledby="paths">
          <div className="wrap">
            <p className="eyebrow">Tumhara sawaal kya hai?</p>
            <h2 id="paths">Har shaadi ka sawaal alag hai. <em>Chart mein dekhne ki cheezein bhi.</em></h2>
            <p className="lede">Apni situation chuno. Dekho Aura kya padhta hai, aur jawaab kaisa dikhta hai.</p>

            <div className="paths">
              <div className="tabs" role="tablist" aria-label="Marriage question">
                <button className="tab" role="tab" id="t-kab" aria-controls="p-kab" aria-selected="true" data-path="kab"><b>Shaadi kab?</b><span>Timing, der kyun</span></button>
                <button className="tab" role="tab" id="t-rishta" aria-controls="p-rishta" aria-selected="false" data-path="rishta" tabIndex={-1}><b>Ye rishta?</b><span>Arranged, haan ya na</span></button>
                <button className="tab" role="tab" id="t-love" aria-controls="p-love" aria-selected="false" data-path="love" tabIndex={-1}><b>Love marriage</b><span>Kundli nahi mili</span></button>
                <button className="tab" role="tab" id="t-parents" aria-controls="p-parents" aria-selected="false" data-path="parents" tabIndex={-1}><b>Parents</b><span>Beti ya bete ke liye</span></button>
              </div>

              <div className="panel" role="tabpanel" id="p-kab" aria-labelledby="t-kab">
                <div>
                  <h3>Sabki ho gayi. Meri kab?</h3>
                  <ul className="fears"><li>Har function mein wahi sawaal, wahi nazrein.</li><li>"Umar nikal jaayegi" ghar pe roz sunne ko milta hai.</li><li>Lagta hai jaise sab aage badh gaye aur tum ruke ho.</li></ul>
                  <p className="looks">Chart mein kya dekha jaata hai</p>
                  <ul className="sigs"><li><b>7th house &amp; 7th lord</b> &middot; shaadi ka main indicator</li><li><b>Venus &amp; Jupiter</b> &middot; marriage karaka</li><li><b>Navamsa (D9)</b> &middot; shaadi ka detailed chart</li><li><b>Running dasha</b> &middot; favourable window kab khulta hai</li></ul>
                </div>
                <div>
                  <div className="msg user">Shaadi mein itni der kyun ho rahi hai?</div>
                  <div className="msg aura"><span className="who">Aura AI</span>Tumhare 7th house pe Saturn ki drishti hai, jise traditionally shaadi mein der, par stable rishte ka indication maana jaata hai. Abhi chal raha Rahu period commitment ke liye slow phase hai. <strong>Venus antardasha</strong> agle saal se, jo traditionally favourable window hai. Der ka matlab "kuch galat" nahi hai.</div>
                  <div className="panel-cta"><button className="btn" type="button" data-ask data-preset="kab">Meri shaadi ki timing dekho &rarr;</button></div>
                </div>
              </div>

              <div className="panel" role="tabpanel" id="p-rishta" aria-labelledby="t-rishta" hidden>
                <div>
                  <h3>Ek meeting. Ek hafta. Zindagi bhar ka faisla.</h3>
                  <ul className="fears"><li>Sab khush hain, toh "na" bolna mushkil hai.</li><li>Ek do meeting mein insaan kitna samajh aata hai?</li><li>Galat haan ka darr, aur achha rishta chhootne ka bhi.</li></ul>
                  <p className="looks">Chart mein kya dekha jaata hai</p>
                  <ul className="sigs"><li><b>Ashtakoot gun milan</b> &middot; 8 kootas, 36 gun</li><li><b>Mangal dosha</b> &middot; dono charts mein, parihar ke saath</li><li><b>Nadi &amp; Bhakoot</b> &middot; sabse zyada poochhe jaane wale</li><li><b>Score ka matlab</b> &middot; kaunsa koota kam, aur kitna matter karta hai</li></ul>
                </div>
                <div>
                  <div className="msg user">Rishta aaya hai, 22 gun mile. Haan bolun?</div>
                  <div className="msg aura"><span className="who">Aura AI</span>22/36 average se upar hai. Nadi aur Bhakoot dono theek hain, aur koi Mangal dosha mismatch nahi hai. <strong>Graha Maitri kam</strong> hai, jo traditionally alag soch aur decisions lene ke alag tareeke ki taraf indicate karta hai. Ye rukne ki wajah nahi, <strong>baat karne ki wajah</strong> hai.</div>
                  <div className="panel-cta"><button className="btn" type="button" data-ask data-preset="rishta-haan">Humari kundli milao &rarr;</button></div>
                </div>
              </div>

              <div className="panel" role="tabpanel" id="p-love" aria-labelledby="t-love" hidden>
                <div>
                  <h3>Pyaar ghar pe samjhana mushkil. Kundli ne aur mushkil kar diya.</h3>
                  <ul className="fears"><li>"Kundli nahi mili" ab bahaana ban gaya hai.</li><li>Number pata hai, matlab kisi ko nahi.</li><li>Na ghar chhod sakte, na unhe chhod sakte.</li></ul>
                  <p className="looks">Chart mein kya dekha jaata hai</p>
                  <ul className="sigs"><li><b>Har koota alag se</b> &middot; kam score kahan se aaya</li><li><b>Traditional parihar</b> &middot; jo shastron mein maane gaye</li><li><b>5th &amp; 7th house</b> &middot; prem aur shaadi ka link</li><li><b>Plain language</b> &middot; family ko dikhane layak explanation</li></ul>
                </div>
                <div>
                  <div className="msg user">Humare 14 gun mile. Papa mana kar rahe hain. Kya karein?</div>
                  <div className="msg aura"><span className="who">Aura AI</span>14 ka zyada hissa <strong>Bhakoot</strong> se kam hua hai. Gana aur Graha Maitri dono achhe hain, yaani temperament aur soch mel khaate hain. Bhakoot ke kuch traditional parihar hain jo tumhare charts mein check kiye ja sakte hain. Faisla tumhara aur tumhari family ka hai.</div>
                  <div className="panel-cta"><button className="btn" type="button" data-ask data-preset="love-family">Humara score samjhao &rarr;</button></div>
                </div>
              </div>

              <div className="panel" role="tabpanel" id="p-parents" aria-labelledby="t-parents" hidden>
                <div>
                  <h3>Bachche ki khushi. Aur ek sahi rishta dhoondhne ki zimmedaari.</h3>
                  <ul className="fears"><li>Teen biodata, teen pandit, teen alag jawaab.</li><li>Rishtedaar poochte hain, "abhi tak kuch fix nahi hua?"</li><li>Galat faisla ho gaya toh zindagi bhar ka afsos.</li></ul>
                  <p className="looks">Chart mein kya dekha jaata hai</p>
                  <ul className="sigs"><li><b>Kai matches ek saath</b> &middot; ek hi logic se compare</li><li><b>Mangal &amp; Nadi dosha</b> &middot; har match ke liye</li><li><b>Score ke saath matlab</b> &middot; kya strong, kya dhyaan dene wala</li><li><b>Aapki bhasha mein</b> &middot; Hindi ya Hinglish</li></ul>
                </div>
                <div>
                  <div className="msg user">Beti ke liye teen rishte hain. Kaunsa behtar hai?</div>
                  <div className="msg aura"><span className="who">Aura AI</span>Teeno ko ek hi logic se milaya. Pehle match mein Nadi dosha hai; doosre aur teesre mein nahi. <strong>Teesre match</strong> mein Gana aur Graha Maitri dono strong hain, jo traditionally achhi samajh ka indication hai. Kundli ek pehlu hai; milna, baat karna, aur beti ki raay utni hi zaroori hai.</div>
                  <div className="panel-cta"><button className="btn" type="button" data-ask data-preset="parents">Rishte compare karo &rarr;</button></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* GUN MILAN. This page reads a score; it does not compute one. The
            calculator itself lives on /kundli-matching and is linked out to,
            deliberately, so the two pages do not compete. */}
        <section className="rule" aria-labelledby="milan">
          <div className="wrap milan">
            <div>
              <p className="eyebrow">Kundli milan</p>
              <h2 id="milan">18 gun mile. <em>Par matlab kisi ne samjhaya?</em></h2>
              <p className="lede">Aura poora Ashtakoot gun milan, Mangal dosha aur Nadi dosha check karta hai. Phir simple bhasha mein batata hai ki score tum dono ke liye kya matlab rakhta hai: kaunsa koota kam hai, kyun, aur kitna matter karta hai.</p>
              <div className="milan-cta">
                <button className="btn" type="button" data-ask data-preset="gun">Mera score samjhao &rarr;</button>
                <Link className="btn ghost" to="/kundli-matching">Gun milan calculator &rarr;</Link>
                <Link className="btn ghost" to="/manglik-calculator">Mangal dosha calculator &rarr;</Link>
              </div>
            </div>
            <div className="score" aria-label="Sample gun milan result">
              <div className="reading-head"><span className="tag">Sample match</span><span className="tag">Ashtakoot</span></div>
              <div className="score-top"><b>18</b><span>/ 36 gun</span></div>
              <div className="bar"><i></i></div>
              <div className="koot">
                <div><b>1/1</b>Varna</div><div className="low"><b>0.5/2</b>Vashya</div><div><b>1.5/3</b>Tara</div><div className="low"><b>1/4</b>Yoni</div>
                <div className="low"><b>0/5</b>Graha Maitri</div><div><b>6/6</b>Gana</div><div className="low"><b>0/7</b>Bhakoot</div><div><b>8/8</b>Nadi</div>
              </div>
              <div className="meaning"><span className="who">Matlab kya hai</span>Gana aur Nadi poore mile, isliye Nadi dosha nahi hai aur temperament mel khaata hai. Kam score Bhakoot aur Graha Maitri se aaya, jo traditionally paise aur family decisions ko alag tareeke se handle karne ki taraf indicate karta hai. Baat karne wali cheez, akele "na" ki wajah nahi.</div>
            </div>
          </div>
        </section>

        {/* DEPTH */}
        <section className="rule" aria-labelledby="depth">
          <div className="wrap">
            <p className="eyebrow">Why this read is different</p>
            <h2 id="depth">Teen pandit. Teen alag jawaab. <em>Ye wala nahi badlega.</em></h2>
            <div className="depth">
              <div className="typ">
                <h3>Typical quick reading</h3>
                <ul><li>Sirf gun ka number</li><li>"Manglik hai, mat karo"</li><li>"Jald hi hogi" jaisi timing</li><li>Aur saath mein 11,000 ki puja</li></ul>
              </div>
              <div className="aura">
                <h3>Aura ek saath kya padhta hai</h3>
                <ul>
                  <li>7th house &amp; 7th lord<span>Shaadi ka main indicator</span></li>
                  <li>Navamsa (D9)<span>Shaadi ka detailed chart</span></li>
                  <li>Venus &amp; Jupiter<span>Marriage karaka</span></li>
                  <li>Running dasha<span>Favourable window kab</span></li>
                  <li>Ashtakoot, 8 kootas<span>Score + har koota ka matlab</span></li>
                  <li>Mangal &amp; Nadi dosha<span>Traditional parihar ke saath</span></li>
                  <li>1,000+ yogas<span>Vivah se jude yog bhi</span></li>
                  <li>Same logic every time<span>Do baar poochho, ek jawaab</span></li>
                </ul>
              </div>
            </div>
            <div className="stats">
              <div><b>10M+</b><span>real charts read</span></div>
              <div><b>10K+</b><span>Vedic rules</span></div>
              <div><b>24x7</b><span>instant answers</span></div>
              <div><b>Swiss</b><span>Ephemeris astronomy data</span></div>
            </div>
            <div className="ps" role="table" aria-label="Problem and solution">
              <div className="ps-row ps-h" role="row"><span role="columnheader">Problem</span><span role="columnheader">Aura ka solution</span></div>
              <div className="ps-row" role="row"><p role="cell">Pandit ji ne sirf number bataya: 18/36.</p><p role="cell"><b>Score + matlab.</b> Kaunsa koota kam, kyun, aur kitna matter karta hai.</p></div>
              <div className="ps-row" role="row"><p role="cell">"Manglik" sunte hi baat ruk gayi.</p><p role="cell"><b>Poora Mangal dosha check:</b> placement, dono charts, aur traditional parihar conditions.</p></div>
              <div className="ps-row" role="row"><p role="cell">"Shaadi kab?" pe sirf "jald hi".</p><p role="cell"><b>Dasha-based windows:</b> 7th lord, Venus aur Jupiter ke periods se.</p></div>
              <div className="ps-row" role="row"><p role="cell">Dosha suna, aur saath mein puja ka bill.</p><p role="cell"><b>Samjhata hai, bechta nahi.</b> Na puja package, na ratan.</p></div>
              <div className="ps-row" role="row"><p role="cell">Ghar pe ye sab khul ke pooch nahi sakte.</p><p role="cell"><b>Private, encrypted, 24/7.</b> Raat ke 2 baje bhi poochho.</p></div>
            </div>
          </div>
        </section>

        {/* REPORT + HOW, one section. Two stacked sections were saying one thing. */}
        <section className="rule" aria-labelledby="report">
          <div className="wrap">
            <p className="eyebrow">What you get</p>
            <h2 id="report">Haan bolne se pehle, <em>poori picture.</em></h2>
            <div className="mrg-duo">
              <div className="report">
                <div className="report-head"><b>Shaadi &amp; Kundli Milan Report</b><span>Sample preview</span></div>
                <ol>
                  <li><b>Shaadi ki timing</b><span>Dasha se favourable windows. Fixed date nahi, windows.</span></li>
                  <li><b>7th house, Venus &amp; D9</b><span>Tumhara chart shaadi aur partner ke baare mein kya indicate karta hai</span></li>
                  <li className="blur" aria-hidden="true"><b>Mangal dosha</b><span>Placement, strength aur traditional parihar</span></li>
                  <li className="blur" aria-hidden="true"><b>Kundli milan</b><span>Ashtakoot score + har koota ka matlab</span></li>
                  <li className="blur" aria-hidden="true"><b>Follow-ups</b><span>Har naye rishte ke baare mein poochho, 24/7</span></li>
                </ol>
                <div className="report-lock">
                  <p>Andar: Mangal dosha analysis, poora kundli milan aur follow-up sawaal.</p>
                  <button className="btn ghost" type="button" data-report>Report kholo &rarr;</button>
                </div>
              </div>

              <div className="mrg-steps">
                <p className="looks">60 second mein pehla jawaab</p>
                <ol className="steps">
                  <li><b>Birth details daalo</b><span>Date, time aur jagah. Milan ke liye partner ki bhi.</span></li>
                  <li><b>Kundli banti hai</b><span>Swiss Ephemeris astronomy data se.</span></li>
                  <li><b>100+ placements ek saath</b><span>Houses, D9, dashas, transits aur yogas.</span></li>
                  <li><b>Simple bhasha mein jawaab</b><span>Phir normal chat ki tarah follow-up poochho.</span></li>
                </ol>
                <button className="btn" type="button" data-ask>Apna sawaal poochho, FREE &rarr;</button>
              </div>
            </div>
          </div>
        </section>

        {/* The page converts in Hinglish and ranks in English. These are the
            terms it is built for, and forcing them into the hero would cost
            more in conversion than it gains in rank, so they live here. */}
        <section className="rule mrg-seo" aria-labelledby="seo">
          <div className="wrap">
            <p className="eyebrow">Marriage astrology</p>
            <h2 id="seo">What a birth chart says about marriage</h2>

            <div className="mrg-seo__grid">
              <article>
                <h3>When will I get married?</h3>
                <p>
                  Vedic astrology reads marriage timing from the 7th house and
                  its lord, Venus and Jupiter as the marriage karakas, the
                  Navamsa or D9 chart, and the Vimshottari dasha you are running.
                  Together these mark favourable windows, not a wedding date.
                  Anyone handing you an exact date from a chart is guessing.
                </p>
              </article>

              <article>
                <h3>Marriage prediction by date of birth</h3>
                <p>
                  A marriage reading needs the date, the time and the place, not
                  the date alone. Those three fix the lagna, the house cusps and
                  the dasha sequence. With only a date you get the planetary
                  signs but not the houses, and the 7th house is what makes it a
                  marriage reading at all.
                </p>
              </article>

              <article>
                <h3>What a gun milan score means</h3>
                <p>
                  Ashtakoot milan scores eight kootas out of 36 gunas, and 18 is
                  the usual threshold. The number alone says little: Nadi and
                  Bhakoot carry the most weight, so a high score with a Nadi
                  dosha is a different situation from a middling score without
                  one. It is a starting point for a conversation, not a verdict.
                </p>
              </article>

              <article>
                <h3>Mangal dosha, honestly</h3>
                <p>
                  Mangal dosha is common, and the classical texts describe many
                  parihar or cancellation conditions: Mars in its own sign, an
                  aspect from Jupiter, or the same dosha in both charts. A chart
                  can show you the placement and those conditions. It cannot
                  tell you whether to marry someone.
                </p>
              </article>
            </div>
          </div>
        </section>

        {/* PRICING. Real figures from PricingContext; the design shipped a
            dashed "price" placeholder chip. */}
        <section className="rule" id="plans" aria-labelledby="offer">
          <div className="wrap">
            <p className="eyebrow">Pricing</p>
            <h2 id="offer">Free se shuru karo. <em>Haan bolne se pehle gehrai mein jao.</em></h2>
            <div className="plans">
              <div className="plan">
                <h3>Free sawaal</h3>
                <div className="price">&#8377;0</div>
                <ul><li>1 shaadi sawaal, tumhare chart se</li><li>Tumhari kundli</li><li>Koi card nahi</li></ul>
                <button className="btn ghost" type="button" data-ask>Free Poochho</button>
              </div>
              <div className="plan star">
                <h3>Shaadi &amp; Kundli Milan Report</h3>
                <div className="price">{reportPrice ? formatPrice(reportPrice) : "₹0"}</div>
                <ul><li>Shaadi ki favourable windows</li><li>7th house, Venus aur D9 analysis</li><li>Mangal dosha + parihar</li><li>Poora kundli milan + matlab</li><li>Report pe follow-up sawaal</li></ul>
                <button className="btn" type="button" data-report>Meri Report Chahiye</button>
              </div>
              <div className="plan">
                <h3>Membership</h3>
                <div className="price">Monthly</div>
                <ul><li>Unlimited follow-up sawaal</li><li>Har naye rishte pe poochho, 24/7</li><li>Kabhi bhi cancel karo</li></ul>
                <button className="btn ghost" type="button" data-subscribe>Membership Dekho</button>
              </div>
            </div>
            <p className="pay">UPI, cards, wallets &middot; Turant access</p>
          </div>
        </section>

        {/* FAQ. Every question here is also in FAQ_SCHEMA above, in this order.
            Edit both together or the structured data goes invalid. */}
        <section className="rule" aria-labelledby="faq">
          <div className="wrap">
            <p className="eyebrow">Questions</p>
            <h2 id="faq">Poochne se pehle.</h2>
            <div className="faq">
              <details><summary>Kya ye meri shaadi ki exact date batayega?</summary><p>Nahi. Vedic timing favourable windows dikhati hai, fixed dates nahi. Aura batata hai ki kaunsa period shaadi ke liye traditionally favourable hai, aur kyun.</p></details>
              <details><summary>Main manglik hoon. Kya meri shaadi mein problem hogi?</summary><p>Mangal dosha bahut common hai, aur iske kai traditional parihar maane jaate hain. Aura tumhara exact placement aur parihar conditions dikhata hai. Ye tumhari shaadi ka faisla nahi karta, sirf samjhata hai.</p></details>
              <details><summary>Kundli nahi mili. Shaadi karein ya nahi?</summary><p>Aura batata hai ki score kahan se kam hua aur uska traditional matlab kya hai. Faisla tumhara aur tumhari family ka hai. Kundli ek pehlu hai, poori kahani nahi.</p></details>
              <details><summary>Kya ye bata sakta hai ki ladka ya ladki achha insaan hai?</summary><p>Nahi. Aura dono charts ki compatibility indications padhta hai, kisi ka character ya private life nahi. Insaan ko jaanne ke liye milna aur baat karna zaroori hai.</p></details>
              <details><summary>Birth time nahi pata?</summary><p>Chalega. Approximate time se bhi chart ban jaata hai aur zyada tar cheezein padhi ja sakti hain. Exact time hone se lagna aur dasha timing sharper hoti hai, isliye jitna paas ho sake utna accurate time daalo.</p></details>
              <details><summary>Free sawaal ke baad kya?</summary><p>Tumhe poora jawaab milta hai. Agar timing windows, Mangal dosha analysis aur poora kundli milan chahiye, toh Marriage Report mein milega.</p></details>
              <details><summary>App download karna padega?</summary><p>Nahi. Yahin browser mein chalta hai. <a href="https://play.google.com/store/apps/details?id=com.astroaura.auraai" rel="noopener" target="_blank">Android app</a> bhi hai.</p></details>
              <details><summary>When will I get married according to my birth chart?</summary><p>Vedic astrology reads marriage timing from the 7th house and its lord, Venus and Jupiter as the marriage karakas, the Navamsa or D9 divisional chart, and the Vimshottari dasha period you are currently running. Together these mark favourable windows rather than a fixed date. Any service offering you an exact wedding date from a birth chart is guessing.</p></details>
              <details><summary>What is marriage prediction by date of birth?</summary><p>A marriage prediction needs the date, the time and the place of birth, not the date alone. Those three fix the lagna, the house cusps and the dasha sequence, which is where the timing comes from. With only a date you get the planetary signs but not the houses, and the 7th house is what makes it a marriage reading.</p></details>
              <details><summary>Is AI marriage prediction free and accurate?</summary><p>Your first question to Aura AI is free, with no card. Accuracy comes from the chart work: Aura computes your kundli with Swiss Ephemeris astronomy, then reads the 7th house, Venus, Jupiter, Navamsa and dashas the classical Vedic way. It shows traditionally favourable windows and compatibility indications, never fixed dates or guarantees.</p></details>
            </div>
          </div>
        </section>

        {/* CLOSE. Privacy is folded in here rather than given its own section. */}
        <section className="mrg-close" aria-labelledby="close">
          <div className="mrg-close__card">
            <h2 id="close" className="mrg-close__title">
              Rishtedaar poochenge hi. <em>Tum pehle apna chart poochho.</em>
            </h2>
            <p className="mrg-close__sub">
              Tumhari aur tumhare partner ki birth details encrypted hain aur kabhi beche nahi jaate. Partner ki details sirf unki permission se daalo.
            </p>
            <button className="btn mrg-close__cta" type="button" data-ask>
              Apna Shaadi Sawaal Poochho, FREE <span aria-hidden="true">&rarr;</span>
            </button>
            <p className="mrg-close__note">
              Pehla sawaal free &middot; Koi card nahi &middot; Private &amp; encrypted
            </p>
            <p className="mrg-close__note">
              Aage explore karo: <Link to="/kundli-matching">kundli matching</Link>{" "}
              &middot; <Link to="/manglik-calculator">manglik dosha calculator</Link>{" "}
              &middot; <Link to="/free-kundli">free kundli</Link>
            </p>
          </div>
        </section>

        <div className="mrg-foot">
          <div className="wrap">
            <span>For guidance purposes. Aura AI offers astrological insights, not guarantees. Marriage decisions are yours and your family's to make.</span>
            <span>
              <Link to="/privacy-policy">Privacy</Link> &middot; <Link to="/terms-and-conditions">Terms</Link>
            </span>
          </div>
        </div>
      </main>

      <AuthModal
        isOpen={authOpen}
        onClose={() => setAuthOpen(false)}
        onGoogleSignIn={signInWithGoogle}
        phoneAuth={phoneAuthHandlers}
        firebasePhoneAuth={firebasePhoneAuthHandlers}
        msg91Auth={msg91AuthHandlers}
      />
      <SubscribeSheet
        isOpen={subscribeOpen}
        onClose={() => setSubscribeOpen(false)}
        source="marriage_page"
      />
    </div>
  );
};

export default MarriagePage;
